import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Copy, Gift } from 'lucide-react';
import { buildReferralUrl } from '../../utils/referral';
import { useReferrals } from '../../hooks/useReferrals';
import { useCopyReferralLink } from '../../hooks/useCopyReferralLink';
interface ReferralBannerProps {
  className?: string;
  /** Matches the surrounding surface — the app shell is dark, reports are light. */
  tone?: 'dark' | 'light';
}
const TONE = {
  dark: {
    shell: 'border-gray-800 bg-[#0A0A0A]',
    badge: 'border-[#FFD700]/30 bg-[#FFD700]/10 text-[#FFD700]',
    title: 'text-white',
    body: 'text-gray-400',
    secondary: 'border-gray-700 text-white hover:bg-white/5',
    ring: 'focus:ring-offset-[#050505]'
  },
  light: {
    shell: 'border-slate-200 bg-white',
    badge: 'border-amber-200 bg-amber-50 text-amber-600',
    title: 'text-slate-900',
    body: 'text-slate-500',
    secondary: 'border-slate-300 text-slate-700 hover:bg-slate-50',
    ring: 'focus:ring-offset-white'
  }
} as const;
/**
 * Compact viral-loop prompt. Reusable on the dashboard, completed scan
 * results and low-credit states. Intentionally quiet so it never competes
 * with a forensic report.
 */
export function ReferralBanner({
  className = '',
  tone = 'dark'
}: ReferralBannerProps) {
  const navigate = useNavigate();
  const {
    referralCode,
    loading
  } = useReferrals();
  const {
    copied,
    copy
  } = useCopyReferralLink(buildReferralUrl(referralCode));
  const style = TONE[tone];
  return <aside aria-label="Referral offer" className={`flex flex-col gap-4 rounded-2xl border p-5 sm:flex-row sm:items-center sm:justify-between ${style.shell} ${className}`} data-id="element-1489" data-mp="24ipz0uhgk8eq">
      <div className="flex items-start gap-3" data-id="element-1490" data-mp="1vitglalnw5s4">
        <span className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${style.badge}`} data-id="element-1491" data-mp="1vk4kdaf3xdj8">
          <Gift size={16} aria-hidden="true" data-id="element-1492" data-mp="1lkdlyxnfeedv" />
        </span>
        <div className="min-w-0" data-id="element-1493" data-mp="1lm6xorcwbosq">
          <p className={`text-sm font-black uppercase tracking-wide ${style.title}`} data-id="element-1494" data-mp="1bwrkvxenf96h">
            Give 5. Get 5. 🚀
          </p>
          <p className={`mt-1 text-sm leading-snug ${style.body}`} data-id="element-1495" data-mp="1gww43v6xyi0e">
            Invite a friend to run their first scan and you both receive 5 free
            scan credits.
          </p>
        </div>
      </div>
      <div className="flex shrink-0 gap-2" data-id="element-1496" data-mp="16zho1woxjv8m">
        <button type="button" onClick={() => navigate('/dashboard/referrals')} className={`flex-1 whitespace-nowrap rounded-xl bg-[#FF0000] px-4 py-3 text-xs font-black uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-[#D90000] focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:ring-offset-2 ${style.ring} sm:flex-none`} data-id="element-1497" data-mp="1umhies9sq923">
          Invite a friend
        </button>
        <button type="button" onClick={copy} disabled={loading || !referralCode} className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl border px-4 py-3 text-xs font-black uppercase tracking-wider transition-colors duration-200 ease-out disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:ring-offset-2 ${style.secondary} ${style.ring} sm:flex-none`} data-id="element-1498" data-mp="2dkk6n4uis1yu">
          {copied ? <Check size={14} aria-hidden="true" data-id="element-1499" data-mp="bqnu5g4mgr86" /> : <Copy size={14} aria-hidden="true" data-id="element-1500" data-mp="3k4n0nbp7zuzx" />}
          {copied ? 'Copied ✓' : 'Copy link'}
        </button>
      </div>
    </aside>;
}